// ProgressRing — today's dose-vs-target visual. Subsequent updates use
// the CSS transition wired in `.ring-fg`; the one-time entrance is
// orchestrated by framer-motion so the ring draws from empty to target
// on mount (rather than snapping into place).

import { motion } from 'framer-motion';
import { EASE_EMPHASIS, useReducedMotion } from '../../lib/motion';

interface ProgressRingProps {
  actual: number;
  target: number;
  size?: number;
  stroke?: number;
}

export function ProgressRing({ actual, target, size = 120, stroke = 10 }: ProgressRingProps) {
  const reduce = useReducedMotion();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const safeTarget = target > 0 ? target : Math.max(actual, 1);
  const pct = Math.max(0, Math.min(1, actual / safeTarget));
  const offset = c * (1 - pct);
  const over = actual > target;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="ring-bg" />
      <motion.circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        strokeWidth={stroke}
        className={`ring-fg ${over ? 'ring-fg-over' : ''}`}
        strokeDasharray={c}
        // framer animates strokeDashoffset on mount from full circle to
        // the target offset. After mount, the CSS transition on .ring-fg
        // owns subsequent updates so the value tracks dose changes.
        initial={reduce ? { strokeDashoffset: offset } : { strokeDashoffset: c }}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: reduce ? 0 : 0.8, ease: EASE_EMPHASIS }}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}
